import { BaseScene } from './BaseScene';
import { GameScene } from './GameScene';
import { SoundManager } from '../managers/SoundManager';
import { StorageManager, LeaderboardEntry } from '../managers/StorageManager';

export class UIScene extends BaseScene {
  // private gameScene!: GameScene;
  // private statusText!: Phaser.GameObjects.Text;
  // private coinsText!: Phaser.GameObjects.Text;

  // The overlay behind the modal
  private modalOverlay!: Phaser.GameObjects.Rectangle;

  // The container for the settings menu
  private settingsMenu!: Phaser.GameObjects.Container;
  private isSettingsOpen: boolean = false;
  
  // Sound management
  private soundManager: SoundManager;
  private storageManager: StorageManager;
  private pauseSound!: Phaser.Sound.BaseSound;
  private unpauseSound!: Phaser.Sound.BaseSound;
  private endgameSound!: Phaser.Sound.BaseSound;
  private soundOnSound!: Phaser.Sound.BaseSound;
  private soundEffectsButton!: Phaser.GameObjects.Text;
  private musicButton!: Phaser.GameObjects.Text;

  constructor() {
    super('UIScene', { active: true });
    this.soundManager = SoundManager.getInstance();
    this.storageManager = StorageManager.getInstance();
  }

  init(_data: { gameScene: GameScene }) {
    // this.gameScene = data.gameScene;
  }

  preload() {
    // Load UI assets
    this.load.image('hamburger_icon', 'assets/images/menu_button.png');
    this.load.audio('sound_on', 'assets/sounds/sound.ogg');
    this.load.audio('pause', 'assets/sounds/pause.ogg');
    this.load.audio('unpause', 'assets/sounds/unpause.ogg');
    this.load.audio('endgame', 'assets/sounds/endgame.ogg');
  }

  create() {
    // Initialize sound objects
    this.pauseSound = this.sound.add('pause');
    this.unpauseSound = this.sound.add('unpause');
    this.endgameSound = this.sound.add('endgame');
    this.soundOnSound = this.sound.add('sound_on');
    
    // Create the main UI elements
    this.createGameStatusUI();
    this.createHamburgerMenu();

    // This scene has its own camera that doesn't move or zoom
    this.cameras.main.setScroll(0, 0);
    
    // Listen for menu-activated event from MenuScene
    this.scene.get('MenuScene').events.on('menu-activated', this.resetUIForMenu, this);
    
    // Listen for game-resumed event from GameScene (with null check)
    const gameScene = this.scene.get('GameScene');
    if (gameScene) {
      gameScene.events.on('game-resumed', this.setupUIForGameplay, this);
    }
  }

  update() {
    // Update UI elements
    this.updateStatusText();
    // this.updateCoinsText();
  }

  private createGameStatusUI() {
    // Create status text
    // this.statusText = this.add.text(10, 10, '', {
    //   fontSize: '24px',
    //   color: '#ffffff',
    // });

    // Create coins display
    // this.coinsText = this.add.text(10, 50, '', {
    //   fontSize: '24px',
    //   color: '#ffff00',
    // });

    // Add other UI elements...
  }

  private createHamburgerMenu() {
    // Create hamburger button in the top-right corner
    const hamburgerButton = this.add.image(
      this.cameras.main.width - 30,
      30,
      'hamburger_icon'
    )
      .setInteractive()
      .setDisplaySize(60, 60)
      .setOrigin(1, 0) // Anchor top-right
      .on('pointerdown', () => this.toggleSettingsMenu());

    // 1) Create a semi-transparent overlay (fullscreen)
    this.modalOverlay = this.add.rectangle(
      0,
      0,
      this.cameras.main.width,
      this.cameras.main.height,
      0x000000,
      0.5
    )
      .setOrigin(0)
      .setScrollFactor(0)
      .setDepth(999) // Above normal UI
      .setVisible(false)
      .setInteractive(); // So it blocks clicks behind the modal

    // 2) Create the container for the settings menu, centered
    //    We'll center the container on the screen by default
    this.settingsMenu = this.add.container(
      this.cameras.main.width / 2,
      this.cameras.main.height / 2
    )
      .setDepth(1000)
      .setVisible(false);

    // The background panel for the menu
    const panel = this.add.rectangle(
      0,
      0,
      300,
      350, // Make the panel slightly taller to fit the new button
      0x333333,
      0.9
    )
      .setOrigin(0.5);

    // We'll position the buttons relative to the panel's center
    const resumeButton = this.createButton(0, -120, 'Resume', () => this.toggleSettingsMenu());
    const soundEffectsButton = this.soundEffectsButton = this.createButton(0, -40, `Sound Effects: ${this.soundManager.soundEffectsEnabled ? 'ON' : 'OFF'}`, () => this.toggleSoundEffects());
    const musicButton = this.musicButton = this.createButton(0, 40, `Music: ${this.soundManager.musicEnabled ? 'ON' : 'OFF'}`, () => this.toggleMusic());
    const quitButton = this.createButton(0, 120, 'Main Menu', () => this.quitGame());

    // Add them to the settings container
    this.settingsMenu.add([panel, resumeButton, soundEffectsButton, musicButton, quitButton]);

    // Handle resizing so the modal stays centered
    this.scale.on('resize', (gameSize: Phaser.Structs.Size) => {
      this.modalOverlay.setSize(gameSize.width, gameSize.height);
      hamburgerButton.setPosition(gameSize.width - 30, 30);

      // Re-center the menu if open
      this.settingsMenu.setPosition(gameSize.width / 2, gameSize.height / 2);
    });
  }

  private toggleSettingsMenu() {
    this.isSettingsOpen = !this.isSettingsOpen;
    this.settingsMenu.setVisible(this.isSettingsOpen);
    this.modalOverlay.setVisible(this.isSettingsOpen);

    // Play appropriate sound
    if (this.isSettingsOpen) {
      if (this.soundManager.soundEffectsEnabled) this.pauseSound.play();
      
      // Pause other scenes that are active
      if (this.scene.get('GameScene') && this.scene.get('GameScene').scene.isActive()) {
        this.scene.pause('GameScene');
      }
      if (this.scene.get('MapScene') && this.scene.get('MapScene').scene.isActive()) {
        this.scene.pause('MapScene');
      }
      if (this.scene.get('StationScene') && this.scene.get('StationScene').scene.isActive()) {
        this.scene.pause('StationScene');
      }
    } else {
      if (this.soundManager.soundEffectsEnabled) this.unpauseSound.play();
      
      // Only resume scenes that are actually paused
      if (this.scene.get('GameScene') && this.scene.get('GameScene').scene.isPaused()) {
        this.scene.resume('GameScene');
      }
      if (this.scene.get('MapScene') && this.scene.get('MapScene').scene.isPaused()) {
        this.scene.resume('MapScene');
      }
      if (this.scene.get('StationScene') && this.scene.get('StationScene').scene.isPaused()) {
        this.scene.resume('StationScene');
      }
    }
  }

  private toggleSoundEffects() {
    // Toggle sound effects state using the manager
    const soundEffectsEnabled = this.soundManager.toggleSoundEffects();
    
    // Update the button text
    this.soundEffectsButton.setText(`Sound Effects: ${soundEffectsEnabled ? 'ON' : 'OFF'}`);
    
    // Play the sound toggle effect if sound effects are now enabled
    if (soundEffectsEnabled) {
      this.soundOnSound.play();
    }
  }

  private toggleMusic() {
    // Toggle music state using the manager
    const musicEnabled = this.soundManager.toggleMusic();
    
    // Update the button text
    this.musicButton.setText(`Music: ${musicEnabled ? 'ON' : 'OFF'}`);
    
    // Play the sound effect for toggling if sound effects are enabled
    // This should happen regardless of whether music is being turned on or off
    if (this.soundManager.soundEffectsEnabled) {
      this.soundOnSound.play();
    }
    
    // Check if we're in the GameScene - if so, we should not play the menu theme
    if (musicEnabled && this.soundManager.isInGameContext(this)) {
      // We're in a game context, so stop any menu music that might have started playing
      const currentTheme = this.soundManager.getCurrentTheme();
      if (currentTheme && currentTheme.isPlaying) {
        currentTheme.stop();
        // We still want to remember that music is enabled
        this.soundManager.setThemePlaying(true);
      }
    }
    
    // No need to manually handle theme music here - the SoundManager's toggleMusic method 
    // already properly handles pausing and resuming the current theme
  }

  private toggleSound() {
    // This method is replaced by separate toggleSoundEffects and toggleMusic methods
    // We'll keep it for backward compatibility but make it update both buttons
    const soundEnabled = this.soundManager.toggleSound();
    
    // Update both button texts
    this.soundEffectsButton.setText(`Sound Effects: ${soundEnabled ? 'ON' : 'OFF'}`);
    this.musicButton.setText(`Music: ${soundEnabled ? 'ON' : 'OFF'}`);
    
    // Play the sound on effect if sound is now enabled
    if (soundEnabled) {
      this.soundOnSound.play();
    }
    
    // Check if we're in the GameScene - if so, we should not play the menu theme
    if (soundEnabled && this.soundManager.isInGameContext(this)) {
      // We're in a game context, so stop any menu music that might have started playing
      const currentTheme = this.soundManager.getCurrentTheme();
      if (currentTheme && currentTheme.isPlaying) {
        currentTheme.stop();
        // We still want to remember that music is enabled
        this.soundManager.setThemePlaying(true);
      }
    }
  }

  private quitGame() {
    // Play endgame sound if sound effects are enabled
    if (this.soundManager.soundEffectsEnabled) {
      this.endgameSound.play();
    }
    
    // Always close the settings menu when quitting
    this.isSettingsOpen = false;
    if (this.settingsMenu) {
      this.settingsMenu.setVisible(false);
    }
    if (this.modalOverlay) {
      this.modalOverlay.setVisible(false);
    }
    
    // Check if GameScene exists and is active/sleeping/paused AND MenuScene is not active
    // This ensures we're actually transitioning from game to menu
    const gameScene = this.scene.get('GameScene');
    const menuScene = this.scene.get('MenuScene');
    const isGameSceneRelevant = gameScene && (
      gameScene.scene.isActive() || 
      gameScene.scene.isSleeping() || 
      gameScene.scene.isPaused()
    );
    const isMenuSceneActive = menuScene && menuScene.scene.isActive();
    
    // Only restart the menu music if we're coming from game to menu (not if already in menu)
    if (isGameSceneRelevant && !isMenuSceneActive && this.soundManager.musicEnabled) {
      this.soundManager.restartMenuMusic();
    }
    
    // Check if MenuScene exists and is initialized
    if (menuScene && (menuScene.scene.isActive() || menuScene.scene.isSleeping())) {
      // If it exists but is asleep, just wake it
      this.scene.wake('MenuScene');
    } else {
      // If it doesn't exist or isn't initialized, start it
      this.scene.start('MenuScene');
    }
    
    // Handle game scenes based on their current state
    if (gameScene) {
      if (gameScene.scene.isPaused()) {
        // If the scene is paused (which is likely if coming from settings menu)
        this.scene.resume('GameScene');  // Resume it first to ensure proper shutdown
        this.scene.stop('GameScene');    // Then stop it rather than just sleeping it
      } else if (gameScene.scene.isActive()) {
        this.scene.stop('GameScene');    // Stop active scene
      } else if (gameScene.scene.isSleeping()) {
        this.scene.wake('GameScene');    // Wake sleeping scene first
        this.scene.stop('GameScene');    // Then stop it
      }
    }
    
    // Handle other game scenes similarly
    const mapScene = this.scene.get('MapScene');
    if (mapScene) {
      if (mapScene.scene.isPaused()) {
        this.scene.resume('MapScene');
        this.scene.stop('MapScene');
      } else if (mapScene.scene.isActive()) {
        this.scene.stop('MapScene');
      } else if (mapScene.scene.isSleeping()) {
        this.scene.wake('MapScene');
        this.scene.stop('MapScene');
      }
    }
    
    const stationScene = this.scene.get('StationScene');
    if (stationScene) {
      if (stationScene.scene.isPaused()) {
        this.scene.resume('StationScene');
        this.scene.stop('StationScene');
      } else if (stationScene.scene.isActive()) {
        this.scene.stop('StationScene');
      } else if (stationScene.scene.isSleeping()) {
        this.scene.wake('StationScene');
        this.scene.stop('StationScene');
      }
    }
    
    // Reset UI for menu context
    this.resetUIForMenu();
  }
  
  /**
   * Reset UI elements for the menu context
   */
  private resetUIForMenu() {
    // Hide any game-specific UI elements
    if (this.settingsMenu) {
      this.settingsMenu.setVisible(false);
    }
    
    if (this.modalOverlay) {
      this.modalOverlay.setVisible(false);
    }
    
    // Reset any other UI state as needed
    this.isSettingsOpen = false;
  }

  private updateStatusText() {
    // Example:
    // const role = this.gameScene.getPlayerRole();
    // const statusMessage = role === 'runner' ? 'Evading capture!' : 'Chasing runner!';
    // this.statusText.setText(`Role: ${role.toUpperCase()} - ${statusMessage}`);
  }

  // private updateCoinsText() {
  //   this.coinsText.setText(`Coins: ${this.gameScene.getCoins()}`);
  // }

  /**
   * Helper for creating text-based buttons at x,y with a callback.
   */
  protected createButton(x: number, y: number, label: string, callback: () => void) {
    const btn = this.add.text(x, y, label, {
      fontSize: '20px',
      color: '#ffffff',
      backgroundColor: '#666666',
      padding: { x: 10, y: 5 },
    })
      .setOrigin(0.5)
      .setInteractive();

    btn.on('pointerdown', callback);

    return btn;
  }

  /**
   * Set up UI for gameplay mode
   */
  private setupUIForGameplay() {
    console.log('UIScene: setupUIForGameplay');
    
    // Make sure we're in gameplay UI mode
    // You can add specific UI changes needed for gameplay here
    
    // Reset settings menu state
    this.isSettingsOpen = false;
    if (this.settingsMenu) {
      this.settingsMenu.setVisible(false);
    }
    if (this.modalOverlay) {
      this.modalOverlay.setVisible(false);
    }
    
    // Ensure any paused scenes are properly resumed or stopped
    // This helps prevent the white screen bug when transitioning from menu to game
    if (this.scene.get('GameScene') && this.scene.get('GameScene').scene.isPaused()) {
      // First resume the scene to ensure it's in a good state
      this.scene.resume('GameScene');
    }
    
    if (this.scene.get('MapScene') && this.scene.get('MapScene').scene.isPaused()) {
      this.scene.resume('MapScene');
    }
    
    if (this.scene.get('StationScene') && this.scene.get('StationScene').scene.isPaused()) {
      this.scene.resume('StationScene');
    }
  }

  // Make sure to clean up event listeners
  shutdown() {
    // Clean up the event listeners with null checks
    const menuScene = this.scene.get('MenuScene');
    if (menuScene) {
      menuScene.events.off('menu-activated', this.resetUIForMenu, this);
    }
    
    const gameScene = this.scene.get('GameScene');
    if (gameScene) {
      gameScene.events.off('game-resumed', this.setupUIForGameplay, this);
    }
  }

  /**
   * Display the leaderboard UI
   * @param role Optional role to filter by
   */
  public showLeaderboard(role?: 'runner' | 'chaser'): void {
    // Create leaderboard container if it doesn't exist
    if (!this.leaderboardContainer) {
      this.createLeaderboardUI();
    }
    
    // Update leaderboard content
    this.updateLeaderboardContent(role);
    
    // Show the leaderboard
    this.modalOverlay.setVisible(true);
    this.leaderboardContainer.setVisible(true);
    
    // Pause game scenes if active
    if (this.scene.get('GameScene') && this.scene.get('GameScene').scene.isActive()) {
      this.scene.pause('GameScene');
    }
    if (this.scene.get('MapScene') && this.scene.get('MapScene').scene.isActive()) {
      this.scene.pause('MapScene');
    }
    if (this.scene.get('StationScene') && this.scene.get('StationScene').scene.isActive()) {
      this.scene.pause('StationScene');
    }
  }
  
  /**
   * Hide the leaderboard UI
   */
  public hideLeaderboard(): void {
    if (this.leaderboardContainer) {
      this.leaderboardContainer.setVisible(false);
    }
    this.modalOverlay.setVisible(false);
    
    // Resume game scenes if they were active
    if (this.scene.get('GameScene') && this.scene.get('GameScene').scene.isPaused()) {
      this.scene.resume('GameScene');
    }
    if (this.scene.get('MapScene') && this.scene.get('MapScene').scene.isPaused()) {
      this.scene.resume('MapScene');
    }
    if (this.scene.get('StationScene') && this.scene.get('StationScene').scene.isPaused()) {
      this.scene.resume('StationScene');
    }
  }
  
  // Property for the leaderboard container
  private leaderboardContainer!: Phaser.GameObjects.Container;
  private leaderboardEntriesContainer!: Phaser.GameObjects.Container;
  
  /**
   * Create the leaderboard UI components
   */
  private createLeaderboardUI(): void {
    // Create container centered on screen
    this.leaderboardContainer = this.add.container(
      this.cameras.main.width / 2,
      this.cameras.main.height / 2
    )
      .setDepth(1000)
      .setVisible(false);
    
    // Create background panel
    const panel = this.add.rectangle(
      0,
      0,
      500,
      600,
      0x333333,
      0.9
    ).setOrigin(0.5);
    
    // Add title
    const title = this.add.text(
      0,
      -250,
      'Leaderboard',
      {
        fontSize: '32px',
        color: '#ffffff',
        fontStyle: 'bold'
      }
    ).setOrigin(0.5);
    
    // Add filter buttons
    const allButton = this.createButton(-150, -200, 'All Players', () => this.updateLeaderboardContent());
    const runnerButton = this.createButton(0, -200, 'Runners', () => this.updateLeaderboardContent('runner'));
    const chaserButton = this.createButton(150, -200, 'Chasers', () => this.updateLeaderboardContent('chaser'));
    
    // Create a container for the scrollable entries
    this.leaderboardEntriesContainer = this.add.container(0, -50);
    
    // Add close button
    const closeButton = this.createButton(0, 250, 'Close', () => this.hideLeaderboard());
    
    // Add all components to the container
    this.leaderboardContainer.add([
      panel,
      title,
      allButton,
      runnerButton,
      chaserButton,
      this.leaderboardEntriesContainer,
      closeButton
    ]);
    
    // Handle window resizing
    this.scale.on('resize', (gameSize: Phaser.Structs.Size) => {
      if (this.leaderboardContainer) {
        this.leaderboardContainer.setPosition(gameSize.width / 2, gameSize.height / 2);
      }
    });
  }
  
  /**
   * Update the leaderboard content with entries
   * @param role Optional role to filter by
   */
  private updateLeaderboardContent(role?: 'runner' | 'chaser'): void {
    // Clear existing entries
    this.leaderboardEntriesContainer.removeAll(true);
    
    // Get leaderboard entries
    let entries: LeaderboardEntry[];
    if (role) {
      entries = this.storageManager.getLeaderboardByRole(role);
    } else {
      entries = this.storageManager.getTopScores(20);
    }
    
    // If no entries, show a message
    if (entries.length === 0) {
      const noEntriesText = this.add.text(
        0,
        0,
        'No leaderboard entries yet.\nPlay a game to set a score!',
        {
          fontSize: '20px',
          color: '#ffffff',
          align: 'center'
        }
      ).setOrigin(0.5);
      
      this.leaderboardEntriesContainer.add(noEntriesText);
      return;
    }
    
    // Create header row
    const rankHeader = this.add.text(-200, -80, 'Rank', { fontSize: '18px', color: '#ffffff', fontStyle: 'bold' }).setOrigin(0.5);
    const nameHeader = this.add.text(-80, -80, 'Name', { fontSize: '18px', color: '#ffffff', fontStyle: 'bold' }).setOrigin(0.5);
    const scoreHeader = this.add.text(40, -80, 'Score', { fontSize: '18px', color: '#ffffff', fontStyle: 'bold' }).setOrigin(0.5);
    const roleHeader = this.add.text(130, -80, 'Role', { fontSize: '18px', color: '#ffffff', fontStyle: 'bold' }).setOrigin(0.5);
    const dateHeader = this.add.text(200, -80, 'Date', { fontSize: '18px', color: '#ffffff', fontStyle: 'bold' }).setOrigin(0.5);
    
    this.leaderboardEntriesContainer.add([rankHeader, nameHeader, scoreHeader, roleHeader, dateHeader]);
    
    // Add each entry - limit to 15 for display
    const displayEntries = entries.slice(0, 15);
    displayEntries.forEach((entry, index) => {
      const yPos = -40 + (index * 30);
      
      // Format date
      const date = new Date(entry.date);
      const dateString = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
      
      // Create entry row
      const rankText = this.add.text(-200, yPos, `${index + 1}`, { fontSize: '16px', color: '#ffffff' }).setOrigin(0.5);
      const nameText = this.add.text(-80, yPos, entry.playerName, { fontSize: '16px', color: '#ffffff' }).setOrigin(0.5);
      const scoreText = this.add.text(40, yPos, entry.score.toString(), { fontSize: '16px', color: '#ffffff' }).setOrigin(0.5);
      
      // Color-code the role
      const roleColor = entry.role === 'runner' ? '#55ff55' : '#ff5555';
      const roleText = this.add.text(130, yPos, entry.role, { fontSize: '16px', color: roleColor }).setOrigin(0.5);
      
      const dateText = this.add.text(200, yPos, dateString, { fontSize: '16px', color: '#ffffff' }).setOrigin(0.5);
      
      this.leaderboardEntriesContainer.add([rankText, nameText, scoreText, roleText, dateText]);
    });
  }
  
  /**
   * Save a score to the leaderboard
   * @param playerName The player's name
   * @param score The score to save
   * @param role The player's role ('runner' or 'chaser')
   */
  public saveScore(playerName: string, score: number, role: 'runner' | 'chaser'): void {
    const entry: LeaderboardEntry = {
      playerName,
      score,
      role,
      date: Date.now()
    };
    
    this.storageManager.saveLeaderboardEntry(entry);
  }
}
